import React from 'react';
import { MapPin, Phone, Clock, Mail, ExternalLink } from 'lucide-react';
import { businessInfo, openingHours } from '../mockData';
import { Card, CardContent } from './ui/card';

export const ContactSection = () => {
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${businessInfo.address.street}, ${businessInfo.address.zip} ${businessInfo.address.city}`
  )}`;

  return (
    <section id="contact" className="py-16 sm:py-20 lg:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Besuchen Sie uns <span className="text-blue-600">in Velbert</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Wir freuen uns auf Ihren Besuch oder Ihre Kontaktaufnahme
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Information */}
          <div className="space-y-6">
            {/* Address Card */}
            <Card className="border-2 hover:border-blue-200 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-xl flex-shrink-0">
                    <MapPin className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Adresse</h3>
                    <p className="text-gray-600 leading-relaxed">
                      {businessInfo.address.street}<br />
                      {businessInfo.address.zip} {businessInfo.address.city}<br />
                      {businessInfo.address.country}
                    </p>
                    <a 
                      href={googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 mt-3 text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                    >
                      <span>Route berechnen</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Phone Card */}
            <Card className="border-2 hover:border-blue-200 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-xl flex-shrink-0">
                    <Phone className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Telefon</h3>
                    <a 
                      href={`tel:${businessInfo.phone}`}
                      className="text-xl font-bold text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      {businessInfo.phone}
                    </a>
                    <p className="text-gray-600 mt-1">
                      Rufen Sie uns direkt an für eine schnelle Beratung
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Opening Hours Card */}
            <Card className="border-2 hover:border-blue-200 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-xl flex-shrink-0">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-grow">
                    <h3 className="font-bold text-gray-900 mb-3">Öffnungszeiten</h3>
                    <div className="space-y-2">
                      {openingHours.map((schedule, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="text-gray-600">{schedule.day}</span>
                          <span className="font-semibold text-gray-900">{schedule.hours}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Map */}
          <div className="relative">
            <Card className="border-2 h-full min-h-[400px] lg:min-h-[600px] overflow-hidden">
              <CardContent className="p-0 h-full">
                {/* Map Placeholder - Click to open Google Maps */}
                <a 
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-full relative group"
                >
                  <img 
                    src="https://images.unsplash.com/photo-1634562876572-5abe57afcceb?w=800&q=80"
                    alt="Standort Velbert"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-blue-600/80 flex items-center justify-center group-hover:bg-blue-600/90 transition-colors">
                    <div className="text-center text-white p-6">
                      <MapPin className="w-16 h-16 mx-auto mb-4" />
                      <p className="text-2xl font-bold mb-2">Kamper Str. 8</p>
                      <p className="text-xl mb-4">42555 Velbert</p>
                      <div className="inline-flex items-center space-x-2 bg-white text-blue-600 px-6 py-3 rounded-full font-semibold">
                        <span>In Google Maps öffnen</span>
                        <ExternalLink className="w-5 h-5" />
                      </div>
                    </div>
                  </div>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
